import React, { useEffect, useRef, useState } from 'react';
import TopModal from '@common_components/modals/topModal.component';
import ConfirmationModal from '@common_components/modals/confirmationModal.component';
import { useTranslation } from 'react-i18next';

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  noConfirmationModal?: boolean;
  icon?: React.ReactNode;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  isLoading?: boolean;
  loadingMessage?: string;
}


const BaseModal: React.FC<BaseModalProps> = ( {
  isOpen,
  onClose,
  icon,
  title,
  children,
  footer,
  className = '',
  noConfirmationModal = false,
  isLoading = false,
  loadingMessage = '',
} ) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>( null );
  const [ isConfirmationOpen, setIsConfirmationOpen ] = useState( false );

  useEffect( () => {
    const body = document.body;
    if ( isOpen ) {
      body.setAttribute( 'data-prev-overflow', body.style.overflow );
      body.style.overflow = 'hidden';

      return () => {
        body.style.overflow = body.getAttribute( 'data-prev-overflow' ) || '';
      };
    } else {
      body.style.overflow = body.getAttribute( 'data-prev-overflow' ) || '';
    }
  }, [ isOpen ] );

  useEffect( () => {
    if ( !isOpen ) {
      return;
    }
    const handleKeyDown = ( e: KeyboardEvent ) => {
      if ( e.key === 'Escape' ) {
        if ( !noConfirmationModal ) {
          setIsConfirmationOpen( true );
        } else {
          onClose();
        }
      }
    };
    window.addEventListener( 'keydown', handleKeyDown );
    return () => {
      window.removeEventListener( 'keydown', handleKeyDown );
    };
  }, [ isOpen, noConfirmationModal, onClose ] );

  const handleClose = () => {
    if ( !noConfirmationModal ) {
      setIsConfirmationOpen( true );
    } else {
      document.body.style.overflow = 'unset';
      onClose();
    }
  };

  const handleOverlayClick = ( e: React.MouseEvent<HTMLDivElement> ) => {
    if ( modalRef.current && !modalRef.current.contains( e.target as Node ) ) {
      handleClose();
    }
  };

  const handleConfirmClose = () => {
    setIsConfirmationOpen( false );
    document.body.style.overflow = 'unset';
    onClose();
  };

  const handleCancelClose = () => {
    setIsConfirmationOpen( false );
  };

  if ( !isOpen ) {
    return ;
  }

  return (
    <>
      {
        isOpen ? (
          <>
            <div
              className="fixed inset-0 flex h-screen w-screen items-center justify-center bg-black bg-opacity-30 z-50"
              onMouseDown={handleOverlayClick}
            >
              <div
                ref={modalRef}
                className={`
                  relative
                  flex flex-col
                  max-h-[95vh] h-fit w-11/12 sm:w-10/12 md:w-9/12 lg:w-7/12 max-w-[1100px] min-w-[320px]
                  items-center justify-center
                  rounded-xl bg-white-base drop-shadow-xl appear-top
                  ${className}
                `}
                onMouseDown={e => { e.stopPropagation(); }}
              >
                {isLoading && (
                  <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50 rounded-xl">
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-500 mb-4"></div>
                      <span className="text-lg text-gray-700 font-medium">{loadingMessage || t( 'common:loading' )}</span>
                    </div>
                  </div>
                )}

                <div className='flex-shrink-0 w-full'>
                  <TopModal icon={icon} title={title || ''} onClose={handleClose} noConfirmationModal={true} />
                </div>

                <div className='flex-1 w-full overflow-y-auto overflow-x-hidden'>
                  { children }
                </div>

                <div className='flex-shrink-0 w-full'>
                  { footer }
                </div>
              </div>
            </div>
            <ConfirmationModal
              isOpen={isConfirmationOpen}
              onConfirm={handleConfirmClose}
              onCancel={handleCancelClose}
              title={t( 'common:unsaved_changes' )}
              description={t( 'common:unsaved_changes_will_be_lost' )}
            />
          </>
        ) : null
      }
    </>
  );
};

export default BaseModal;
